import { Component } from '@theme/component';

/**
 * Review Wall.
 *
 * Every review is rendered server-side by Liquid and the "Show more" reveal is pure CSS
 * (a chain of visually hidden checkboxes + `<label>`s, driven by the `:nth-child` rules the
 * section emits in `{% style %}`). This component is a progressive enhancement only:
 *
 *  - photo/video lightbox (never available without JS, same as the Dawn original);
 *  - forcing the reveal open when the theme editor selects a review that is still hidden.
 *
 * Public API (see HORIZON-SPEC 10.2): `pause()`, `resume()`, `select(index, event, options)`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} list - The masonry column list holding every review card.
 * @property {HTMLElement} [lightbox] - The fixed lightbox overlay.
 * @property {HTMLElement} [lightboxMedia] - Where the cloned photo/video is mounted.
 * @property {HTMLElement} [lightboxBody] - Where the cloned review copy is mounted.
 * @property {HTMLButtonElement} [closeButton] - The lightbox close button.
 * @property {HTMLInputElement[]} [steps] - The reveal-step checkboxes.
 *
 * @extends Component<Refs>
 */
export class OcReviewWallComponent extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {AbortController | null} */
  #openAbort = null;

  /** @type {Element | null} */
  #lastFocus = null;

  /** @type {boolean} */
  #paused = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // HORIZON-SPEC 10.2 (b): own listener, so a core theme update cannot drop it.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect, { signal });
    }
  }

  disconnectedCallback() {
    this.closeLightbox();
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  /* ------------------------------------------------------------------ *
   * Reveal
   * ------------------------------------------------------------------ */

  /** Every review card, in DOM order. */
  get cards() {
    return /** @type {HTMLElement[]} */ (Array.from(this.refs.list.querySelectorAll('[data-oc-review]')));
  }

  /** The reveal-step checkboxes, in DOM order. */
  get steps() {
    const { steps } = this.refs;
    return Array.isArray(steps) ? steps : steps ? [steps] : [];
  }

  /**
   * Tick every reveal step. The reveal itself is CSS; JS only needs to force it
   * open when a hidden review has to be shown (theme editor selection).
   */
  revealAll() {
    for (const step of this.steps) {
      if (step instanceof HTMLInputElement) step.checked = true;
    }
  }

  /* ------------------------------------------------------------------ *
   * Lightbox
   * ------------------------------------------------------------------ */

  /**
   * Open the lightbox for the photo/video that was clicked.
   *
   * @param {Event} event - The click, retargeted by `component.js` to the `[data-oc-media]` element.
   */
  openLightbox(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const photo = target.closest('[data-oc-media]');
    if (!(photo instanceof HTMLElement) || !this.contains(photo)) return;

    const card = photo.closest('[data-oc-review]');
    if (!card) return;

    event.preventDefault();

    const { lightbox, lightboxMedia, lightboxBody, closeButton } = this.refs;
    if (!lightbox || !lightboxMedia || !lightboxBody) return;

    const source = photo.querySelector('img, video');
    lightboxMedia.innerHTML = '';
    if (source) {
      const media = /** @type {HTMLElement} */ (source.cloneNode(true));
      media.removeAttribute('loading');
      if (media instanceof HTMLVideoElement) {
        media.controls = true;
        media.muted = false;
        media.autoplay = true;
        media.setAttribute('playsinline', '');
      }
      lightboxMedia.appendChild(media);
    }

    const main = card.querySelector('.oc-rw__card-main');
    lightboxBody.innerHTML = '';
    if (main) lightboxBody.appendChild(main.cloneNode(true));

    this.#lastFocus = document.activeElement;
    lightbox.hidden = false;
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Global dismissal, bound only while the lightbox is open — the pattern core
    // Horizon uses for its own dialogs (assets/product-hotspot.js:276-278).
    this.#openAbort = new AbortController();
    const { signal } = this.#openAbort;
    document.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibility, { signal });

    if (closeButton instanceof HTMLElement) closeButton.focus();
  }

  /** Close the lightbox and restore focus. */
  closeLightbox() {
    const { lightbox, lightboxMedia, lightboxBody } = this.refs;
    if (!lightbox || !lightbox.classList.contains('is-open')) return;

    this.#openAbort?.abort();
    this.#openAbort = null;

    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.hidden = true;
    if (lightboxMedia) lightboxMedia.innerHTML = '';
    if (lightboxBody) lightboxBody.innerHTML = '';
    document.body.style.overflow = '';

    const last = this.#lastFocus;
    if (last instanceof HTMLElement && typeof last.focus === 'function') last.focus();
    this.#lastFocus = null;
  }

  /**
   * Close when the click lands on the backdrop itself, never on the dialog.
   * `component.js` retargets `event.target` to the element carrying `on:click`,
   * so the real innermost element is read from the composed path.
   *
   * @param {Event} event - The click event.
   */
  onBackdropClick(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const origin = path[0] instanceof Element ? path[0] : event.target;
    if (origin === this.refs.lightbox) this.closeLightbox();
  }

  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape') this.closeLightbox();
  };

  #handleVisibility = () => {
    const { lightboxMedia } = this.refs;
    if (!document.hidden || !lightboxMedia) return;
    const video = lightboxMedia.querySelector('video');
    if (video instanceof HTMLVideoElement) video.pause();
  };

  /* ------------------------------------------------------------------ *
   * Theme editor (HORIZON-SPEC 10.2)
   * ------------------------------------------------------------------ */

  #handleBlockSelect = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const card = event.target.closest('[data-oc-review]');
    if (!(card instanceof HTMLElement) || card.closest('oc-review-wall-component') !== this) return;

    this.pause();
    this.select(this.cards.indexOf(card), undefined, { animate: true });
  };

  #handleBlockDeselect = (/** @type {Event} */ event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('oc-review-wall-component') !== this) return;

    this.resume();
  };

  /**
   * Reveal the review at `index` if the CSS reveal still hides it, then scroll to it.
   *
   * @param {number} index - Index of the review card in DOM order.
   * @param {Event} [_event] - Unused, kept for signature parity with `Slideshow.select`.
   * @param {{ animate?: boolean }} [options] - Whether the scroll animates.
   */
  select(index, _event, { animate = true } = {}) {
    const card = this.cards[index];
    if (!card) return;

    // `offsetParent === null` means the CSS reveal chain still has it at `display: none`.
    if (card.offsetParent === null) this.revealAll();

    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    card.scrollIntoView({ behavior: animate && !reduced ? 'smooth' : 'auto', block: 'center' });
  }

  /** Pause — no ambient motion here, kept for the 10.2 contract. */
  pause() {
    this.#paused = true;
    this.setAttribute('paused', '');
  }

  /** Resume — no ambient motion here, kept for the 10.2 contract. */
  resume() {
    this.#paused = false;
    this.removeAttribute('paused');
  }

  get paused() {
    return this.#paused;
  }
}

if (!customElements.get('oc-review-wall-component')) {
  customElements.define('oc-review-wall-component', OcReviewWallComponent);
}
